import { useEffect, useState } from 'react';
import { Search, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { useChargebackStore } from '../../stores/chargebackStore';

export default function Step2_PartSelection({ onNext, onBack }) {
  const {
    eligibleParts,
    selectedPartIds,
    loadingParts,
    loadEligibleParts,
    togglePart,
    selectAllParts,
    deselectAllParts,
    isStep2Valid,
    getTotalAmount,
    getTotalValue
  } = useChargebackStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('tariff_desc');

  useEffect(() => {
    loadEligibleParts();
  }, []);

  // Filter and sort parts
  const filteredParts = eligibleParts
    .filter(part => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        part.part_number.toLowerCase().includes(query) ||
        part.description.toLowerCase().includes(query) ||
        part.program.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'tariff_desc':
          return b.tariff_cents - a.tariff_cents;
        case 'tariff_asc':
          return a.tariff_cents - b.tariff_cents;
        case 'part_number':
          return a.part_number.localeCompare(b.part_number);
        default:
          return 0;
      }
    });

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const getStatusBadge = (part) => {
    if (part.status === 'complete') {
      return (
        <Badge className="bg-green-100 text-green-800">
          <CheckCircle2 size={12} className="mr-1" />
          Complete
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-orange-600 border-orange-600">
        <AlertTriangle size={12} className="mr-1" />
        Incomplete
      </Badge>
    );
  };

  const allSelected = filteredParts.length > 0 && filteredParts.every(part => selectedPartIds.includes(part.id));

  const handleSelectAll = () => {
    if (allSelected) {
      // Deselect all filtered parts
      const filteredIds = filteredParts.map(p => p.id);
      const remaining = selectedPartIds.filter(id => !filteredIds.includes(id));
      useChargebackStore.setState({ selectedPartIds: remaining });
    } else {
      // Select all filtered parts
      const newIds = [...new Set([...selectedPartIds, ...filteredParts.map(p => p.id)])];
      useChargebackStore.setState({ selectedPartIds: newIds });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Step 2: Select Parts</CardTitle>
          <CardDescription>
            Choose the parts to include in this chargeback
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Filters */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search parts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={handleSelectAll}>
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

          {/* Loading State */}
          {loadingParts && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading parts...</span>
            </div>
          )}

          {/* Parts Table */}
          {!loadingParts && filteredParts.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>No eligible parts found for the selected criteria.</p>
              <Button variant="link" onClick={onBack} className="mt-2">
                Go back and change selection
              </Button>
            </div>
          )}

          {!loadingParts && filteredParts.length > 0 && (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Part Number</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead className="text-right">Tariff Impact</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredParts.map((part) => (
                    <TableRow key={part.id} className="cursor-pointer hover:bg-gray-50">
                      <TableCell>
                        <Checkbox
                          checked={selectedPartIds.includes(part.id)}
                          onCheckedChange={() => togglePart(part.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{part.part_number}</TableCell>
                      <TableCell className="max-w-xs truncate">{part.description}</TableCell>
                      <TableCell>{part.program}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(part.tariff_cents)}
                      </TableCell>
                      <TableCell>{getStatusBadge(part)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Running Total - Sticky Bottom */}
      <div className="sticky bottom-0 bg-white border-t-2 border-blue-600 shadow-lg rounded-t-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Selected Parts</p>
            <p className="text-2xl font-bold text-gray-900">
              {selectedPartIds.length} {selectedPartIds.length === 1 ? 'part' : 'parts'}
            </p>
            {selectedPartIds.length > 0 && (
              <Button
                variant="link"
                size="sm"
                onClick={deselectAllParts}
                className="p-0 h-auto text-red-600"
              >
                Clear all
              </Button>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Tariff</p>
            <p className="text-3xl font-bold text-blue-600">
              {formatCurrency(getTotalAmount())}
            </p>
            <p className="text-xs text-gray-500">
              Value: {formatCurrency(getTotalValue())}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onBack}>
              ← Back
            </Button>
            <Button
              onClick={onNext}
              disabled={!isStep2Valid()}
              size="lg"
            >
              Review →
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
